import { ChangeEvent, KeyboardEvent, useMemo } from 'react';

import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';

import { useMultiValueInput } from './hooks/useMultiValueInput';
import { Tag } from './Tag';

interface NumberValueInputProps {
  value: string | string[];
  onChange: (newValue: string | string[]) => void;
  disabled: boolean;
  filterDefinition: SimpleFilterDefinition;
  allowMultiple?: boolean;
}

export const NumberValueInput = ({
  value,
  onChange,
  disabled,
  filterDefinition,
  allowMultiple = false,
}: NumberValueInputProps) => {
  // Validation function for numbers
  const validate = useMemo(
    () => (val: string) => {
      const num = parseInt(val, 10);
      const { min, max, required } = filterDefinition.validation || {};

      if (required && !val) {
        return {
          valid: false,
          error: `${filterDefinition.uiField} is required`,
        };
      }

      if (isNaN(num)) {
        return {
          valid: false,
          error: `${filterDefinition.uiField} must be a number`,
        };
      }

      if (min !== undefined && num < min) {
        return {
          valid: false,
          error: `${filterDefinition.uiField} must be at least ${min}`,
        };
      }

      if (max !== undefined && num > max) {
        return {
          valid: false,
          error: `${filterDefinition.uiField} must be at most ${max}`,
        };
      }

      return { valid: true };
    },
    [filterDefinition]
  );

  const multiValueState = useMultiValueInput({
    value,
    onChange,
    validate,
    disabled,
  });

  // Single value mode
  if (!allowMultiple) {
    const formatValue = (val: string | string[]): string => {
      return String(val);
    };

    return (
      <input
        type="number"
        disabled={disabled}
        value={formatValue(value)}
        onChange={e => onChange(e.target.value)}
        placeholder={`Enter ${filterDefinition.uiField}...`}
        min={filterDefinition.validation?.min}
        max={filterDefinition.validation?.max}
        required={filterDefinition.validation?.required}
        className={`flex-[3.5] rounded border px-3 py-1.5 text-sm ${
          disabled
            ? 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary cursor-not-allowed opacity-90'
            : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:border-chariot-brand-primary'
        }`}
        aria-label="Filter value"
      />
    );
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    multiValueState.setInputValue(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<Element>) => {
    const isControl =
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
      ].includes(e.key) ||
      e.ctrlKey ||
      e.metaKey;

    // ignore non-numeric inputs
    if (!/^\d$/.test(e.key) && !isControl) {
      e.preventDefault();
      return;
    }

    multiValueState.handleKeyDown(e);
  };

  const handleClickAway = () => {
    if (!multiValueState.inputValue) return;

    multiValueState.handleKeyDown({
      key: 'Enter',
      preventDefault: () => {},
    } as KeyboardEvent);
  };

  // Multi-value tag mode
  return (
    <div
      // eslint-disable-next-line react-hooks/refs
      ref={multiValueState.containerRef}
      className={`flex flex-[3.5] flex-wrap items-center gap-1 rounded border px-3 py-1.5 text-sm ${
        disabled
          ? 'border-chariot-stroke bg-chariot-background-primary cursor-not-allowed opacity-90'
          : 'border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary'
      }`}
      aria-label="Filter value"
    >
      {/* eslint-disable-next-line react-hooks/refs */}
      {multiValueState.tags.map((tag, index) => (
        <Tag
          key={index}
          value={tag}
          onRemove={() => multiValueState.handleRemoveTag(index)}
          disabled={disabled}
        />
      ))}
      {!disabled && (
        <input
          type="number"
          // eslint-disable-next-line react-hooks/refs
          value={multiValueState.inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleClickAway}
          placeholder={`Enter ${filterDefinition.uiField}...`}
          min={filterDefinition.validation?.min}
          max={filterDefinition.validation?.max}
          className="text-chariot-text-primary min-w-[120px] flex-1 [appearance:textfield] border-0 bg-transparent outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          aria-label="Add filter value"
        />
      )}
    </div>
  );
};
